/**
 * <announcement-bar> — cycling announcement bar with crossfade slide swaps.
 *
 * Loaded ONLY from `sections/section-announcement_bar.liquid` via
 * `type="module" fetchpriority="low"`. Does not live in global.js.
 *
 * Dependencies:
 *   - @theme/delegate
 *   - @theme/responsive (batched resize via addResizeListener)
 *   - @theme/scroll-listener (multiplexed scroll subscriber for `--announcement-dynamic-height` sync)
 */

import { Delegate } from '@theme/delegate';
import { responsive } from '@theme/responsive';
import { scrollListener } from '@theme/scroll-listener';

class AnnouncementBar extends HTMLElement {
  /** @type {{ removeResizeListener: () => void } | null} */
  #resizeCleanup = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #resizeDebounceTimer = null;

  /**
   * Holds the gap between a slide fading out and the next fading in. Slides
   * swap sequentially (not as a crossfade): the current one fades out, the bar
   * sits empty for one transition duration, then the next fades in.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #swapTimer = null;

  /** Fallback if the slide's `transition-duration` can't be read (matches CSS `--ann-transition-speed` default). */
  static #SLIDE_TRANSITION_FALLBACK_MS = 500;

  /** rAF throttle handle for scroll-driven `--announcement-dynamic-height` updates. */
  #scrollRafId = null;

  /** @type {(() => void) | null} */
  #scrollRemover = null;

  /** Avoid duplicate listeners / intervals when Shopify re-connects the element without a clean disconnect. */
  #initialized = false;

  /** @type {MediaQueryList | null} */
  #reducedMotionMql = null;

  /** @type {(() => void) | null} */
  #boundReducedMotionChange = null;

  /** Pause auto-rotate while user hovers or focuses a slide. */
  #hoverPaused = false;

  /**
   * Scroll handler — keeps `--announcement-dynamic-height` in sync as the bar
   * scrolls out of view. Without this, sticky elements that anchor to it
   * (e.g. `.faq-tabs`, `.terms-links`) leave a gap below the header until the
   * next slide rotation triggers `#syncDynamicHeight()`.
   */
  #onScroll = () => {
    if (this.#scrollRafId !== null) return;
    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#syncDynamicHeight();
    });
  };

  constructor() {
    super();
    this.element = this;
    this.delegateElement = new Delegate(this.element);
  }

  connectedCallback() {
    if (this.#initialized) {
      return;
    }
    this.#initialized = true;

    this.slides = Array.from(this.element.querySelectorAll('.announcement-bar__slide'));
    this.slidesCount = this.slides.length;

    this.currentIndex = 0;
    this.cycleInterval = null;
    this.displayDuration = parseInt(this.element.dataset.displayDuration, 10) || 4000;

    if (this.slidesCount > 1) {
      this.#reducedMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.#boundReducedMotionChange = () => this.#onReducedMotionChange();
      this.#reducedMotionMql.addEventListener('change', this.#boundReducedMotionChange);
    }

    if (this.slidesCount > 0) {
      this.initSlides();
      this.element.classList.add('is-initialized');
    }

    // Single slide (or only one left after market filtering): no cycling, no
    // transition — `announcement-bar--static` shows it plainly.
    this.element.classList.toggle('announcement-bar--static', this.slidesCount <= 1);

    this.#syncDynamicHeight();
    this.bindEvents();

    if (this.slidesCount > 1) {
      this.#syncPlaybackState();
    }
  }

  disconnectedCallback() {
    this.stopCycle();
    if (this.delegateElement) {
      this.delegateElement.off();
    }
    if (this.#resizeCleanup) {
      this.#resizeCleanup.removeResizeListener();
      this.#resizeCleanup = null;
    }
    clearTimeout(this.#resizeDebounceTimer);
    this.#resizeDebounceTimer = null;
    clearTimeout(this.#swapTimer);
    this.#swapTimer = null;
    this.#scrollRemover?.();
    this.#scrollRemover = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#reducedMotionMql && this.#boundReducedMotionChange) {
      this.#reducedMotionMql.removeEventListener('change', this.#boundReducedMotionChange);
    }
    this.#reducedMotionMql = null;
    this.#boundReducedMotionChange = null;
    this.#hoverPaused = false;
    this.element.classList.remove('is-initialized');
    this.#initialized = false;
  }

  initSlides() {
    this.slides.forEach((slide, index) => {
      slide.removeAttribute('data-slide-index');
      if (index === 0) {
        slide.classList.add('is-active');
        slide.removeAttribute('aria-hidden');
        slide.inert = false;
      } else {
        slide.classList.remove('is-active');
        slide.setAttribute('aria-hidden', 'true');
        // inert also removes the (opacity:0) slide's links from tab order.
        slide.inert = true;
      }
    });
  }

  bindEvents() {
    this.#resizeCleanup = responsive.addResizeListener(this, this.#scheduleResizeUpdate);

    ({ removeScrollListener: this.#scrollRemover } =
      scrollListener.addScrollListener(this, this.#onScroll));

    // mouseover/mouseout bubble — mouseenter/leave do not, so Delegate (which
    // listens on the root) cannot catch them. Guard with relatedTarget so moving
    // between children inside the bar doesn't spuriously unpause.
    this.delegateElement.on('mouseover', '.announcement-bar__slides', (event, target) => {
      const related = /** @type {Node | null} */ (event.relatedTarget);
      if (related && target.contains(related)) return;
      this.#hoverPaused = true;
    });
    this.delegateElement.on('mouseout', '.announcement-bar__slides', (event, target) => {
      const related = /** @type {Node | null} */ (event.relatedTarget);
      if (related && target.contains(related)) return;
      this.#hoverPaused = false;
    });

    this.delegateElement.on('focusin', '.announcement-bar__slide', () => {
      this.#hoverPaused = true;
    });
    this.delegateElement.on('focusout', '.announcement-bar__slide', () => {
      this.#hoverPaused = false;
    });
  }

  /**
   * Debounced height sync — resize callbacks would otherwise fire at display
   * refresh rate and force layout (clientHeight + getBoundingClientRect).
   */
  #scheduleResizeUpdate() {
    clearTimeout(this.#resizeDebounceTimer);
    // timeout: Debounce resize-driven layout reads to avoid per-frame reflow thrashing.
    this.#resizeDebounceTimer = setTimeout(() => {
      this.#resizeDebounceTimer = null;
      this.updateHeight();
      if (this.slidesCount > 1) {
        this.#syncPlaybackState();
      }
    }, 200);
  }

  #onReducedMotionChange() {
    if (this.slidesCount > 1) {
      this.#syncPlaybackState();
    }
  }

  /** Reduced motion: stop auto-rotate; otherwise start. */
  #syncPlaybackState() {
    if (!this.#reducedMotionMql) {
      return;
    }

    if (this.#reducedMotionMql.matches) {
      this.stopCycle();
      return;
    }

    this.startCycle();
  }

  updateHeight() {
    this.#syncDynamicHeight();
  }

  #syncDynamicHeight() {
    const rect = this.element.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    let visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
    visibleHeight = Math.max(visibleHeight, 0);
    // Round to whole px: a fractional value feeds sticky `top` calc()s (e.g. FAQ tab bar) and
    // Safari rounds the sticky position differently than the content beneath, leaving a sub-pixel
    // sliver of text peeking above the bar mid-scroll. Header height is rounded for the same reason.
    // Only --announcement-dynamic-height is scroll-driven; fixed layout vars stay in Liquid CSS.
    document.documentElement.style.setProperty('--announcement-dynamic-height', `${Math.round(visibleHeight)}px`);
  }

  startCycle() {
    this.stopCycle();

    if (this.slidesCount <= 1 || !this.#reducedMotionMql || this.#reducedMotionMql.matches) {
      return;
    }

    this.cycleInterval = setInterval(() => {
      if (!this.#hoverPaused) {
        this.goToSlide((this.currentIndex + 1) % this.slidesCount);
      }
    }, this.displayDuration);
  }

  stopCycle() {
    if (this.cycleInterval) {
      clearInterval(this.cycleInterval);
      this.cycleInterval = null;
    }
  }

  goToSlide(nextIndex) {
    if (nextIndex === this.currentIndex || this.slidesCount <= 1) {
      return;
    }

    const currentSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[nextIndex];

    // Sequential swap (not a crossfade): fade the current slide out first…
    currentSlide.classList.remove('is-active');
    currentSlide.setAttribute('aria-hidden', 'true');
    currentSlide.inert = true;

    this.currentIndex = nextIndex;

    // …then, once it has fully faded out, fade the next one in. The empty gap
    // equals one transition duration, matching the reference announcement bar.
    // timeout: transitionend on currentSlide is unreliable here — opacity may not change
    // (slide already at 0), and an interrupted/reduced-motion swap would never fire it,
    // stalling the rotation. The gap is read from the CSS transition-duration so the two
    // stay in sync. Cleared by clearTimeout above and in disconnectedCallback.
    clearTimeout(this.#swapTimer);
    this.#swapTimer = setTimeout(() => {
      this.#swapTimer = null;
      nextSlide.classList.add('is-active');
      nextSlide.removeAttribute('aria-hidden');
      nextSlide.inert = false;
      this.#syncDynamicHeight();
    }, this.#getSlideTransitionMs());
  }

  /** Reads the slide's CSS `transition-duration` so the swap gap stays in sync with the stylesheet. */
  #getSlideTransitionMs() {
    const slide = this.slides[0];
    if (!slide) {
      return AnnouncementBar.#SLIDE_TRANSITION_FALLBACK_MS;
    }
    const raw = getComputedStyle(slide).transitionDuration.split(',')[0].trim();
    const ms = raw.endsWith('ms') ? parseFloat(raw) : parseFloat(raw) * 1000;
    return Number.isFinite(ms) && ms >= 0 ? ms : AnnouncementBar.#SLIDE_TRANSITION_FALLBACK_MS;
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}
